import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  model,
  resource,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClarityModule, ClrDatagrid, ClrForm, ClrLoadingState } from '@clr/angular';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject } from 'rxjs';
import { ArrayMethodsCallbackPipe } from 'src/app/pipes/array-methods-callback.pipe';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { LinieDrehungPipe } from 'src/app/pipes/linie-drehung.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { LinieDrehungService } from 'src/app/services/Shared/linie-drehung.service';
import { PreviewThumbnailsComponent } from '../../_shared/preview-thumbnails/preview-thumbnails.component';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { Table } from 'src/app/models/ui/table';
import { CachedMediaStore } from 'src/app/shared/stores/media-cached.store';
import { MediaApiFunc } from 'src/app/services/MediaApiFunc/abstract-media-api-func';
import { AnlagenMediaApiFuncService, MEDIA_TITLE } from 'src/app/services/MediaApiFunc/anlagen-media-api-func.service';
import { FileObject } from 'src/app/services/base64/base64.service';
import { EntsperrAnlageService } from 'src/app/services/Shared/entsperr-anlage.service';
import { ConfirmService } from 'src/app/services/confirm.service';
import { DatalistInputComponent } from '../../_shared/datalist-input/datalist-input.component';
import { calcPosition, GeoAutofill } from 'src/app/services/Shared/geo-autofill';
import { calcDirection } from 'src/app/shared/classes/linie-validation-manager';

@Component({
  selector: 'app-naturschutzdialog',
  templateUrl: './naturschutzdialog.component.html',
  styleUrl: './naturschutzdialog.component.scss',
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    PreviewThumbnailsComponent,
    DatalistInputComponent,
    MrTranslatePipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
    LinieDrehungPipe,
    ArrayMethodsCallbackPipe,
  ],
  providers: [
    LinieDrehungPipe,
    { provide: MEDIA_TITLE, useValue: 'Naturschutz' },
    { provide: MediaApiFunc, useClass: AnlagenMediaApiFuncService },
    CachedMediaStore,
    GeoAutofill,
  ],
})
export class NaturschutzdialogComponent {
  private readonly apiService = inject(APIService);
  private readonly toastr = inject(ToastrService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly cachedMedia = inject(CachedMediaStore);
  private readonly confirmService = inject(ConfirmService);
  private readonly geoAutofill = inject(GeoAutofill);
  protected readonly drehungPipe = inject(LinieDrehungPipe);
  protected readonly drehung = inject(LinieDrehungService);

  public readonly closedialog = new BehaviorSubject(false);
  private dataRefresh: EventEmitter<any>;

  private readonly preview = viewChild(PreviewThumbnailsComponent);
  private readonly grid = viewChild(ClrDatagrid);
  private readonly clrForm = viewChild(ClrForm);

  readonly bezeichnung = model<string>(undefined);
  readonly otypid = model<number>(undefined);
  readonly ostammid = model<number>(undefined);
  readonly stammData = model<{ startmeter: number; endmeter: number }>({ startmeter: 0, endmeter: 0 });

  private readonly startmeter = computed(() => this.stammData().startmeter);
  private readonly endmeter = computed(() => this.stammData().endmeter);
  protected readonly direction = computed(() => calcDirection(this.startmeter(), this.endmeter()));
  protected readonly selectedNatur = signal<NaturschutzRow>(undefined);

  protected exceptStartEnd = (col: any) => !['position'].includes(col.id);
  protected readonly table = signal<Table<NaturschutzRow>>({ columns: [], rows: [] });
  private readonly naturartenResource = resource({
    params: this.otypid,
    loader: ({ params }) => this.apiService.getNaturschutzArten(params),
  });
  protected readonly artListe = this.naturartenResource.value;
  protected readonly isInspektion = signal(true);
  protected readonly deletedFiles = signal<string[]>([]);
  protected readonly saveState = signal(ClrLoadingState.DEFAULT);
  protected readonly geoState = signal(ClrLoadingState.DEFAULT);

  protected readonly minMeter = computed(() => Math.min(this.startmeter(), this.endmeter()));
  private readonly maxMeter = computed(() => Math.max(this.startmeter(), this.endmeter()));

  public set data(dataIn: any) {
    this.isInspektion.set(false);
    if (dataIn.table) this.table.set(dataIn.table);
    this.ostammid.set(dataIn.ostamm);
    this.otypid.set(dataIn.otypid);
    this.stammData.set(dataIn.stammData);
    this.bezeichnung.set(dataIn.name);
    this.dataRefresh = dataIn.dataRefresh;
  }

  protected formNaturTop = new FormGroup({
    position: new FormControl<number>(undefined, { updateOn: 'blur' }),
    schutzgrad: new FormControl<string>('', [Validators.required]),
    bezeichnung: new FormControl<string>(''),
    beschreibung: new FormControl<string>(''),
  });

  constructor() {
    inject(EntsperrAnlageService).registerDestroyRef(inject(DestroyRef));

    this.geoAutofill.registerOSTAMMID(this.ostammid);

    effect(() => {
      if (this.ostammid() && (this.isInspektion() || !this.table().columns.length)) untracked(() => this.freshReload());
    });

    effect(() => {
      const selected = this.selectedNatur();
      untracked(() => {
        this.formNaturTop.reset();
        if (!selected) return;
        this.formNaturTop.patchValue(selected);
        this.clrForm().markAsTouched();

        this.cachedMedia.getBilder(selected.id, selected.bild, this.preview());
      });
    });

    effect(() => {
      const min = this.minMeter();
      const max = this.maxMeter();

      const posCtrl = this.formNaturTop.get('position');
      posCtrl.setValidators([Validators.required, Validators.min(min), Validators.max(max)]);
      posCtrl.updateValueAndValidity();
    });
  }

  protected addNatur() {
    this.grid()?.selection.clearSelection();
    this.formNaturTop.reset();

    setTimeout(() => {
      this.preview().clearPreview();
    });
  }

  protected async deleteTitelNatur() {
    const text = this.mrTranslate.transform('Eintrag wirklich löschen?');
    const ok = await this.confirmService.danger(text);
    if (!ok || !this.selectedNatur()) return;

    const id = this.selectedNatur().id;
    const { success, error } = await this.apiService.deleteNaturschutz(id);
    if (success) {
      this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
      this.freshReload();
      this.cachedMedia.deleteInCache(id, this.preview());
      this.grid()?.selection.clearSelection();
      this.dataRefresh?.emit(true);
    } else {
      console.log(error);
      this.toastr.error(this.mrTranslate.transform('Fehler beim Löschen'));
    }
  }

  private async freshReload() {
    const bawerkeTable = await this.apiService.getNaturschutz(this.ostammid());
    this.table.update(table => {
      if (!table?.columns?.length) table = bawerkeTable;
      else table.rows = bawerkeTable.rows;
      return { ...table };
    });
  }

  protected saveIfValid() {
    this.clrForm().markAsTouched();
    if (this.formNaturTop.valid) this.saveNatur();
  }

  private async saveNatur() {
    this.saveState.set(ClrLoadingState.LOADING);

    const sendObj: NaturschutzModel = {
      ID: this.selectedNatur()?.id ?? -1,
      table: this.formNaturTop.value,
      mediaChange: {
        files: await this.preview().packUnsavedFiles(),
        savedFiles: this.preview().savedNames() ?? '',
        deletedFiles: this.deletedFiles(),
      },
    };

    const { success, error, id } = await this.apiService.setNaturschutz(this.ostammid(), sendObj);

    if (success) {
      this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));
      this.cachedMedia.reloadAfterSaved(id, this.preview(), this.deletedFiles());
      this.deletedFiles.set([]);
      this.freshReload();
      this.grid().selected = [{ id }];
      this.dataRefresh?.emit(true);
      this.saveState.set(ClrLoadingState.SUCCESS);
    } else {
      console.log(error);
      this.toastr.error(this.mrTranslate.transform('Fehler beim Speichern'));
      this.saveState.set(ClrLoadingState.DEFAULT);
    }
  }

  public checkLinieValid(startmeter: number, endmeter: number) {
    this.stammData.set({ startmeter, endmeter });

    const valid = this.table().rows.every(row =>
      startmeter < endmeter ? +row.position >= startmeter : +row.position <= startmeter
    );
    if (!valid)
      this.toastr.error(
        this.mrTranslate.transform('Berücksichtigen Sie die Start- & Endmeter'),
        this.mrTranslate.transform('Naturschutz')
      );
    return valid;
  }

  protected async findPositionAndAutofill() {
    this.geoState.set(ClrLoadingState.LOADING);
    const snapped = await this.geoAutofill.getSnappedLength();
    this.geoState.set(ClrLoadingState.DEFAULT);
    if (snapped === null) return;

    const position = calcPosition({
      snapped,
      start: this.startmeter(),
      end: this.endmeter(),
      direction: this.direction(),
    });

    this.formNaturTop.controls.position.setValue(position);
  }

  protected deleteFile(name: string) {
    this.deletedFiles.update(names => (names.push(name), [...names]));
  }

  protected close() {
    this.closedialog.next(true);
  }
}

export interface NaturschutzRow {
  id: number;
  ostammid: number;
  position: number;
  schutzgrad: string;
  bezeichnung: string;
  beschreibung: string;
  bild: string;
  benutzername: string;
  status: string;
}

export interface NaturschutzModel {
  ID: number;
  table: Partial<{
    position: number;
    schutzgrad: string;
    bezeichnung: string;
    beschreibung: string;
  }>;
  mediaChange: {
    files: FileObject[];
    savedFiles: string;
    deletedFiles: string[];
  };
}
